import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_URL } from "../config";

/**
 * UvolneniMustrAdmin — editace mustru emailu „žádost o uvolnění" (admin only).
 * Ukládá se přes /api/preblokace/settings (tenant-wide). Zahrnuje předmět, tělo
 * a příjemce 2–4 (odesílatel je vždy přidán automaticky na backendu).
 */
const DEFAULT_SUBJECT = 'Žádost o uvolnění klienta / {{companyName}} / IČO: {{ico}}';
const DEFAULT_BODY = 'Ahoj,\n\nbylo by prosím možné uvolnit tohoto klienta?\nMáme zde historickou vazbu.\n\nCA: {{ca}}\nPůvodní vlastník: {{prm}}\n\nDěkuji.\n\nS pozdravem\n{{senderName}}';

export default function UvolneniMustrAdmin({ isDarkMode, onClose }) {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [recipient2, setRecipient2] = useState('');
  const [recipient3, setRecipient3] = useState('');
  const [recipient4, setRecipient4] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'
  const bodyRef = useRef(null);

  const bg = isDarkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900";
  const cardBg = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const inputBg = isDarkMode
    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400";

  const markdownToHtml = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/\*\*(.*?)\*\*/gs, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gs, '<em>$1</em>')
      .replace(/__(.*?)__/gs, '<u>$1</u>')
      .replace(/==(.*?)==/gs, '<mark style="background:#ffd700;color:#000;">$1</mark>')
      .replace(/\n/g, '<br>');
  };

  const previewValues = {
    companyName: 'Příklad s.r.o.',
    ico: '12345678',
    ca: '1009999999',
    prm: 'VBCPOOL',
    senderName: 'Jan Novák',
  };
  const applyTemplate = (tpl) =>
    (tpl || '')
      .replace(/\{\{companyName\}\}/g, previewValues.companyName)
      .replace(/\{\{ico\}\}/g, previewValues.ico)
      .replace(/\{\{ca\}\}/g, previewValues.ca)
      .replace(/\{\{prm\}\}/g, previewValues.prm)
      .replace(/\{\{senderName\}\}/g, previewValues.senderName);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('lastUserId') || '';
      const res = await fetch(`${API_URL}/preblokace/settings?userId=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setSubject(data.subjectTemplate || DEFAULT_SUBJECT);
        setBody(data.bodyTemplate || DEFAULT_BODY);
        setRecipient2(data.recipient2 || '');
        setRecipient3(data.recipient3 || '');
        setRecipient4(data.recipient4 || '');
      }
    } catch (e) {
      console.error("Chyba při načítání mustru uvolnění:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg(null);
      const userId = localStorage.getItem('lastUserId') || '';
      const res = await fetch(`${API_URL}/preblokace/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          recipient2,
          recipient3,
          recipient4,
          subjectTemplate: subject,
          bodyTemplate: body,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Nepodařilo se uložit.');
      setSubject(data.subjectTemplate || subject);
      setBody(data.bodyTemplate || body);
      setRecipient2(data.recipient2 || '');
      setRecipient3(data.recipient3 || '');
      setRecipient4(data.recipient4 || '');
      setMsg({ type: "ok", text: "Mustr uložen." });
    } catch (e) {
      setMsg({ type: "err", text: e.message });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  const wrapSelection = (marker) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const val = el.value;
    const newVal = val.substring(0, start) + marker + val.substring(start, end) + marker + val.substring(end);
    setBody(newVal);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + marker.length, end + marker.length);
    });
  };

  return (
    <div className={`min-h-screen p-6 ${bg}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">✉️ Mustr emailu – žádost o uvolnění</h1>
          <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Šablona emailu pro žádost o uvolnění klienta. Platí pro všechny uživatele (může editovat pouze admin).
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            ✕ Zavřít
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-amber-400 border-t-transparent" />
        </div>
      ) : (
        <div className="max-w-3xl space-y-5">
          {/* Předmět */}
          <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
            <label className="text-xs font-semibold uppercase tracking-wide mb-2 block opacity-70">Předmět</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 ${inputBg}`}
            />
          </div>

          {/* Tělo */}
          <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wide opacity-70">Text emailu</label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setViewMode('edit')}
                  className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                    viewMode === 'edit' ? 'bg-amber-500 text-white border-amber-500' : `${inputBg}`
                  }`}>✏️ Upravit</button>
                <button type="button" onClick={() => setViewMode('preview')}
                  className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                    viewMode === 'preview' ? 'bg-amber-500 text-white border-amber-500' : `${inputBg}`
                  }`}>👁 Náhled</button>
              </div>
            </div>
            {viewMode === 'edit' ? (
              <>
                <div className="flex gap-1 mb-2">
                  <button type="button" onClick={() => wrapSelection('**')} className="px-2 py-1 text-xs font-bold rounded border border-gray-300 dark:border-gray-600">B</button>
                  <button type="button" onClick={() => wrapSelection('*')} className="px-2 py-1 text-xs italic rounded border border-gray-300 dark:border-gray-600">I</button>
                  <button type="button" onClick={() => wrapSelection('__')} className="px-2 py-1 text-xs underline rounded border border-gray-300 dark:border-gray-600">U</button>
                  <button type="button" onClick={() => wrapSelection('==')} className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600">🖍</button>
                </div>
                <textarea
                  ref={bodyRef}
                  rows={12}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  className={`w-full text-sm border rounded-lg px-3 py-3 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono ${inputBg}`}
                />
              </>
            ) : (
              <div
                className={`w-full max-h-96 overflow-y-auto text-sm border rounded-lg px-4 py-3 leading-relaxed ${cardBg}`}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(applyTemplate(body)) }}
              />
            )}
            <p className="text-xs mt-2 opacity-60">
              Zástupné značky: <code>{'{{companyName}}'}</code> <code>{'{{ico}}'}</code> <code>{'{{ca}}'}</code> <code>{'{{prm}}'}</code> <code>{'{{senderName}}'}</code>
            </p>
          </div>

          {/* Příjemci */}
          <div className={`rounded-xl border p-5 shadow-sm ${cardBg}`}>
            <label className="text-xs font-semibold uppercase tracking-wide mb-2 block opacity-70">Další příjemci (kromě odesílatele)</label>
            <div className="space-y-2">
              <input type="email" placeholder="Příjemce 2 (email)" value={recipient2} onChange={e => setRecipient2(e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 ${inputBg}`} />
              <input type="email" placeholder="Příjemce 3 (email)" value={recipient3} onChange={e => setRecipient3(e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 ${inputBg}`} />
              <input type="email" placeholder="Příjemce 4 (email)" value={recipient4} onChange={e => setRecipient4(e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 ${inputBg}`} />
            </div>
            <p className="text-xs mt-2 opacity-60">Odesílatel (přihlášený uživatel) je vždy přidán automaticky.</p>
          </div>

          <div className="flex items-center justify-end gap-3">
            {msg && (
              <span className={`text-sm font-medium ${msg.type === "ok" ? "text-green-500" : "text-red-500"}`}>
                {msg.type === "ok" ? "✅ " : "❌ "}{msg.text}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-5 py-2 rounded-lg font-semibold text-sm transition ${
                saving ? "bg-gray-400 cursor-not-allowed text-white" : "bg-amber-500 hover:bg-amber-600 text-white"
              }`}
            >
              {saving ? "Ukládám…" : "💾 Uložit mustr"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
